import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "@platform/api";

import {
  finalDecisionToFormValues,
  formValuesToCoverageRecords,
} from "./finalDecisionFormAdapters";
import {
  FinalDecisionFormValues,
  FinalDecisionsWithSubmittedForm,
  RfpFinalDecisionResponse,
  SubmitCoverageDecisionsResponse,
  UnsoldDecisionCoverage,
} from "./types";

interface SubmitCoverageDecisionParams {
  values: FinalDecisionFormValues;
  rfpId: string;
  finalDecisionId: number;
}

export const getFinalDecisionsCacheKey = (rfpId: string): string[] => [
  "rfpFinalDecisions",
  rfpId,
];

// useFinalDecisionQuery fetches a final decision record for a given RFP ID, returns null if none exists
export function useFinalDecisionQuery({
  rfpId,
}: {
  rfpId: string;
}): UseQueryResult<FinalDecisionsWithSubmittedForm | null, Error> {
  const axiosInstance = useApi();
  return useQuery<
    RfpFinalDecisionResponse | null,
    Error,
    FinalDecisionsWithSubmittedForm | null
  >({
    queryKey: getFinalDecisionsCacheKey(rfpId),
    queryFn: () =>
      axiosInstance
        .get<RfpFinalDecisionResponse | null>(
          `brokerportal/v2/final-decisions`,
          {
            params: { rfpId },
          },
        )
        .then((res) => res.data),
    select: finalDecisionToFormValues,
    enabled: !!rfpId,
  });
}

export function useUnsoldCoveragesQuery({
  rfpId,
}: {
  rfpId: string;
}): UseQueryResult<UnsoldDecisionCoverage[], Error> {
  const axiosInstance = useApi();
  return useQuery({
    queryKey: [...getFinalDecisionsCacheKey(rfpId), "unsold"],
    queryFn: () =>
      axiosInstance
        .get<UnsoldDecisionCoverage[]>(
          `brokerportal/v2/final-decisions/coverages/unsold`,
          {
            params: { rfpId },
          },
        )
        .then((res) => res.data),
  });
}

// useInitializeFinalDecision creates or retrieves a final decision draft for a given RFP ID
export function useInitializeFinalDecision(): UseMutationResult<
  RfpFinalDecisionResponse,
  Error
> {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (rfpId: string) => {
      // Skip initialization if we already have FinalDecision cached
      const cachedData = queryClient.getQueryData<RfpFinalDecisionResponse>(
        getFinalDecisionsCacheKey(rfpId),
      );

      if (cachedData) {
        return Promise.resolve({ ...cachedData });
      }
      return axiosInstance
        .post<RfpFinalDecisionResponse>(`brokerportal/v2/final-decisions`, {
          rfpId: parseInt(rfpId),
        })
        .then((res) => res.data);
    },
    onSuccess: (data, rfpId) => {
      // Set the query cache so the drawer can immediately access the data
      queryClient.setQueryData(getFinalDecisionsCacheKey(rfpId), data);
    },
  });

  return mutation;
}

export function useSubmitCoverageDecisionRecords(): UseMutationResult<
  SubmitCoverageDecisionsResponse,
  Error,
  SubmitCoverageDecisionParams
> {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: SubmitCoverageDecisionParams) => {
      const transformedValues = formValuesToCoverageRecords(data.values);
      return axiosInstance
        .put<SubmitCoverageDecisionsResponse>(
          `brokerportal/v2/final-decisions/${data.finalDecisionId}/coverages`,
          {
            ...transformedValues,
          },
        )
        .then((res) => res.data);
    },
    onSuccess: (_, variables) => {
      // Invalidate the query to refetch the final decision data
      queryClient.invalidateQueries({
        queryKey: getFinalDecisionsCacheKey(variables.rfpId),
      });
      // Invalidate prepareRfpDetails to refetch RFP data
      queryClient.invalidateQueries({
        queryKey: ["prepareRfpDetails", variables.rfpId],
      });
    },
  });

  return mutation;
}
